/**
 * Glitch Events — per-turn-cycle chaos modifiers.
 * Rolls at the start of each turn cycle. Uses seeded RNG for multiplayer safety.
 */

import type { GameState } from '../types';
import type { GameEffect } from './effects';
import { random } from '../terrain/rng';
import { isSoloMode } from './mode';
import {
  GLITCH, GLITCH_NONE, GLITCH_REGISTRY,
  GLITCH_MASSIVE_KNOCKBACK, GLITCH_HUGE_RECOIL,
  GLITCH_LOW_GRAVITY, GLITCH_QUAKE_SLAM,
} from '../constants';

/** Roll a glitch for the new turn cycle. Called from advanceTurn on isNewTurn. */
export function rollGlitch(state: GameState, fx: GameEffect[]): void {
  // No glitches in solo modes or turn 1
  if (isSoloMode(state.modeState.mode) || state.turnNumber <= 1) {
    state.activeGlitch = GLITCH_NONE;
    return;
  }

  // 60% chance per turn cycle
  if (random() > GLITCH.ROLL_CHANCE) {
    state.activeGlitch = GLITCH_NONE;
    return;
  }

  // Build tier pool based on turn number
  const pool = GLITCH_REGISTRY.filter(g => {
    if (state.turnNumber <= GLITCH.TIER1_MAX_TURN) return g.tier === 1;
    if (state.turnNumber <= GLITCH.TIER2_MAX_TURN) return g.tier <= 2;
    return true; // all tiers
  });

  if (pool.length === 0) {
    state.activeGlitch = GLITCH_NONE;
    return;
  }

  // Pick random from pool (seeded RNG)
  const pick = pool[Math.floor(random() * pool.length)];
  state.activeGlitch = pick.id;

  // Announce
  const tierColor = pick.tier === 1 ? GLITCH.TIER1_COLOR
    : pick.tier === 2 ? GLITCH.TIER2_COLOR
    : GLITCH.TIER3_COLOR;

  fx.push({
    type: 'announce_screen',
    text: pick.name,
    color: tierColor,
    fontSize: 36,
    hold: 1.2,
    y: 0.25,
  });
  fx.push({
    type: 'announce_screen',
    text: pick.desc,
    color: tierColor,
    fontSize: 18,
    hold: 1.2,
    y: 0.32,
    delay: 0.15,
  });
  fx.push({ type: 'screen_shake', intensity: 6, decay: 0.85 });
}

/** Check if a specific glitch is active. */
export function isGlitchActive(state: GameState, id: number): boolean {
  return state.activeGlitch === id;
}

/** Get knockback multiplier (for MASSIVE_KNOCKBACK). */
export function getGlitchKnockbackMult(state: GameState): number {
  return state.activeGlitch === GLITCH_MASSIVE_KNOCKBACK ? GLITCH.MASSIVE_KNOCKBACK_MULT : 1.0;
}

/** Get gravity multiplier (for LOW_GRAVITY). */
export function getGlitchGravityMult(state: GameState): number {
  return state.activeGlitch === GLITCH_LOW_GRAVITY ? GLITCH.LOW_GRAVITY_MULT : 1.0;
}

/** Get recoil force if HUGE_RECOIL is active, else 0. */
export function getGlitchRecoilForce(state: GameState): number {
  return state.activeGlitch === GLITCH_HUGE_RECOIL ? GLITCH.HUGE_RECOIL_FORCE : 0;
}
